// src/pages/TelcB1LandingPage.jsx. TELC B1 Google Ads campaign landing page
// Same design language as HomePageB: warm stone/amber palette, Space Grotesk headings,
// hand-drawn SVG accents, Embla feature gallery. Hero visual replaced with YouTube lesson.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useTranslation } from 'react-i18next';
import MarketingFooter from '../components/layout/MarketingFooter';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Check, Play, ChevronUp, Quote, Volume2, VolumeX,
} from 'lucide-react';
import { trackConversionEvent } from '../utils/conversionEvents';

// ─── YouTube lesson video ──────────────────────────────────────
const LESSON_VIDEO_ID = 'mOhO8V339Kk';

// ─── Feature demo videos (same as HomePageB) ──────────────────
const STORAGE_BUCKET = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
function localVideoPath(filename) { return `/video/${filename}`; }
function storageUrl(filename, localPath) {
  if (STORAGE_BUCKET) {
    const encoded = encodeURIComponent(`videos/${filename}`);
    return `https://firebasestorage.googleapis.com/v0/b/${STORAGE_BUCKET}/o/${encoded}?alt=media`;
  }
  return localPath;
}
function buildVideoSources(envUrl, filename) {
  const fallbackLocal = localVideoPath(filename);
  const candidates = [envUrl, storageUrl(filename, fallbackLocal), fallbackLocal].filter(Boolean);
  return [...new Set(candidates)];
}
const DEMO_VIDEO_SOURCES = {
  simulator: buildVideoSources(import.meta.env.VITE_DEMO_SIMULATOR_URL, 'demo-exam.mp4'),
  lessons:   buildVideoSources(import.meta.env.VITE_DEMO_LESSONS_URL, 'demo-lessons.mp4'),
  redemittel: buildVideoSources(import.meta.env.VITE_DEMO_REDEMITTEL_URL, 'demo-redemittel.mp4'),
  grammar:   buildVideoSources(import.meta.env.VITE_DEMO_GRAMMAR_URL, 'demo-grammar.mp4'),
  vocab:     buildVideoSources(import.meta.env.VITE_DEMO_VOCAB_URL, 'demo-vocab.mp4'),
};
const DEMO_THUMBNAILS = {
  simulator:  '/images/thumbnails/exam_thumbnail.webp',
  lessons:    '/images/thumbnails/lesson_thumbnail.webp',
  redemittel: '/images/thumbnails/redemittel_thumbnail.webp',
  grammar:    '/images/thumbnails/grammar_thumbnail.webp',
  vocab:      '/images/thumbnails/vocab_thumbnail.webp',
};

// ─── Warm noise grain (same as HomePageB) ─────────────────────
const NOISE_SVG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' fill='%231c1917' opacity='0.025'/%3E%3C/svg%3E")`;

// ─── Teachers (same as HomePageB) ─────────────────────────────
const TEACHERS = [
  { initials: 'ES', name: 'Emir S.', photo: '/images/amir-safari.jpg' },
  { initials: 'MK', name: 'Maria K.', color: '#E8D5B7' },
  { initials: 'SL', name: 'Stefan L.', color: '#C4B5A0' },
  { initials: 'AH', name: 'Anna H.', color: '#D4C4AA' },
];
const TEAM = [
  { initials: 'ES', name: 'Emir Safari', role: 'teamEmir', color: '#1C1917', textColor: '#FAFAF9', photo: '/images/amir-safari.jpg', large: true },
  { initials: 'MK', name: 'Maria K.', role: 'teamMaria', color: '#E8D5B7', textColor: '#44403C' },
  { initials: 'SL', name: 'Stefan L.', role: 'teamStefan', color: '#C4B5A0', textColor: '#44403C' },
  { initials: 'AH', name: 'Anna H.', role: 'teamAnna', color: '#D4C4AA', textColor: '#44403C' },
];

// ─── Features list (TELC B1 specific labels) ──────────────────
const FEATURES = [
  { key: 'simulator',  titleKey: 'homeB.features.simulator',  descKey: 'homeB.features.simulatorDesc' },
  { key: 'lessons',    titleKey: 'homeB.features.lessons',    descKey: 'homeB.features.lessonsDesc' },
  { key: 'grammar',    titleKey: 'homeB.features.grammar',    descKey: 'homeB.features.grammarDesc' },
  { key: 'vocab',      titleKey: 'homeB.features.vocab',      descKey: 'homeB.features.vocabDesc' },
  { key: 'redemittel', titleKey: 'homeB.features.redemittel', descKey: 'homeB.features.redemittelDesc' },
];

// ═══════════════════════════════════════════════════════════════
// TEACHER AVATAR STACK
// ═══════════════════════════════════════════════════════════════
function TeacherAvatarStack({ label, className = '' }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex -space-x-2.5">
        {TEACHERS.map((teacher, i) => (
          teacher.photo ? (
            <img
              key={teacher.initials}
              src={teacher.photo}
              alt={teacher.name}
              className="w-8 h-8 rounded-full object-cover ring-2 ring-white transition-transform duration-200 hover:scale-125 hover:z-50 cursor-default"
              style={{ zIndex: TEACHERS.length - i }}
            />
          ) : (
            <div
              key={teacher.initials}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold ring-2 ring-white transition-transform duration-200 hover:scale-125 hover:z-50 cursor-default"
              style={{ backgroundColor: teacher.color, color: '#44403C', zIndex: TEACHERS.length - i }}
            >
              {teacher.initials}
            </div>
          )
        ))}
      </div>
      <span className="text-[13px] font-medium text-stone-600 leading-tight">{label}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// HAND-DRAWN CIRCLE
// ═══════════════════════════════════════════════════════════════
function HandDrawnCircle({ children }) {
  const draw = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: { pathLength: 1, opacity: 1, transition: { pathLength: { duration: 2.2, ease: [0.43, 0.13, 0.23, 0.96] }, opacity: { duration: 0.4 } } },
  };
  return (
    <span className="relative inline-block">
      {children}
      <motion.svg
        viewBox="0 0 286 72"
        className="absolute -inset-x-3 -inset-y-1 w-[calc(100%+24px)] h-[calc(100%+8px)] pointer-events-none"
        preserveAspectRatio="none"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-40px' }}
        aria-hidden
      >
        {/* Decorative graphic, aria-hidden: deliberately no SVG title element, which
              would be concatenated into the parent heading's text content. */}
        <motion.path
          d="M 18 56 C -8 28, 30 4, 143 8 C 256 4, 294 28, 268 44 C 242 60, 30 64, 18 56"
          fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.4"
          variants={draw}
        />
      </motion.svg>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// HAND-DRAWN LINE (animated underline)
// ═══════════════════════════════════════════════════════════════
function HandDrawnLine({ children }) {
  const draw = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: { pathLength: 1, opacity: 1, transition: { pathLength: { duration: 2.5, ease: [0.43, 0.13, 0.23, 0.96] }, opacity: { duration: 0.5 } } },
  };
  return (
    <span className="relative inline-block">
      {children}
      <motion.svg
        viewBox="0 0 300 16"
        className="absolute -bottom-1 left-0 w-full h-4 pointer-events-none"
        preserveAspectRatio="none"
        initial="hidden"
        animate="visible"
        aria-hidden
      >
        {/* Decorative graphic, aria-hidden: deliberately no SVG title element, which
              would be concatenated into the parent heading's text content. */}
        <motion.path
          d="M 4 11 C 40 5, 80 13, 130 7 C 180 3, 220 12, 260 6 C 275 5, 290 9, 296 8"
          fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.45"
          variants={draw}
        />
      </motion.svg>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// YOUTUBE LESSON PLAYER, warm-framed, replaces AppReel in hero
// ═══════════════════════════════════════════════════════════════
function YouTubeLessonPlayer({ videoId }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative w-full max-w-[380px] sm:max-w-[420px] mx-auto">
      {/* Warm ambient glow, mirrors AppReel's glow */}
      <div className="absolute inset-0 -m-8 bg-gradient-to-br from-amber-100/50 via-transparent to-teal-100/30 blur-3xl rounded-full pointer-events-none" />

      {/* Lesson label pill */}
      <div className="relative flex items-center gap-2 mb-3 justify-center">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-900/90 text-[11px] font-semibold text-white/80 shadow-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          TELC B1. Echte Lektion
        </span>
      </div>

      {/* Video frame, warm stone border, same radius as AppReel phone */}
      <div className="relative rounded-[1.25rem] overflow-hidden border-[3px] border-stone-200 bg-stone-100 shadow-[0_20px_60px_rgba(28,25,23,0.18),0_4px_16px_rgba(28,25,23,0.1)]">
        {/* Shimmer placeholder */}
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-stone-100 to-stone-200" style={{ aspectRatio: '16/9' }}>
            <div className="w-12 h-12 rounded-full bg-stone-900/10 flex items-center justify-center animate-pulse">
              <Play className="w-5 h-5 text-stone-400 fill-current ml-0.5" />
            </div>
          </div>
        )}
        <div style={{ aspectRatio: '16/9' }}>
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&color=white`}
            title="TELC B1 Lesson. Logami"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            onLoad={() => setLoaded(true)}
            className="w-full h-full border-0"
            loading="lazy"
          />
        </div>
      </div>

      {/* Caption */}
      <p className="text-center text-[12px] text-stone-400 mt-2.5">
        Eine echte TELC B1 Lektion, schau rein, bevor du dich anmeldest.
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FEATURE VIDEO GALLERY (identical to HomePageB)
// ═══════════════════════════════════════════════════════════════
function FeatureVideoGallery({ t }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [activeVideoSourceIndex, setActiveVideoSourceIndex] = useState(0);
  const videoRef = useRef(null);

  const activeVideo = activeIndex !== null ? FEATURES[activeIndex] : null;
  const activeVideoSources = activeVideo ? DEMO_VIDEO_SOURCES[activeVideo.key] || [] : [];
  const currentActiveVideoSource =
    activeVideoSources[activeVideoSourceIndex] || activeVideoSources[0] || localVideoPath('demo-exam.mp4');

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: false,
    breakpoints: { '(max-width: 768px)': { dragFree: true } },
  });

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
      setCurrentSlide(emblaApi.selectedScrollSnap());
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => { emblaApi.off('select', onSelect); emblaApi.off('reInit', onSelect); };
  }, [emblaApi]);

  const openVideo = useCallback((index) => { setActiveVideoSourceIndex(0); setActiveIndex(index); }, []);
  const closeVideo = useCallback(() => { setActiveVideoSourceIndex(0); setActiveIndex(null); }, []);
  const goNext = useCallback(() => { setActiveVideoSourceIndex(0); setActiveIndex((prev) => (prev !== null ? (prev + 1) % FEATURES.length : 0)); }, []);
  const goPrev = useCallback(() => { setActiveVideoSourceIndex(0); setActiveIndex((prev) => (prev !== null ? (prev - 1 + FEATURES.length) % FEATURES.length : 0)); }, []);
  const handleActiveVideoError = useCallback(() => {
    setActiveVideoSourceIndex((prev) => prev < activeVideoSources.length - 1 ? prev + 1 : prev);
  }, [activeVideoSources.length]);

  useEffect(() => {
    if (!activeVideo) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeVideo();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [activeVideo, closeVideo, goNext, goPrev]);

  useEffect(() => {
    if (activeVideo && videoRef.current) { videoRef.current.play().catch(() => {}); }
  }, [activeVideo, activeIndex, currentActiveVideoSource]);

  return (
    <>
      <div className="relative">
        {/* Nav arrows, desktop only */}
        <div className="hidden md:flex absolute -top-14 right-4 lg:right-8 gap-2 z-10">
          <button
            onClick={() => emblaApi?.scrollPrev()}
            disabled={!canScrollPrev}
            className="w-10 h-10 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 disabled:opacity-30 disabled:cursor-default transition-colors cursor-pointer"
            aria-label="Previous"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => emblaApi?.scrollNext()}
            disabled={!canScrollNext}
            className="w-10 h-10 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 disabled:opacity-30 disabled:cursor-default transition-colors cursor-pointer"
            aria-label="Next"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex ml-0 2xl:ml-[max(2rem,calc(50vw-700px))]">
            {FEATURES.map((feat, idx) => (
              <div
                key={feat.key}
                className="min-w-0 shrink-0 grow-0 pl-5 basis-[75vw] sm:basis-[55vw] md:basis-[38vw] lg:basis-[30%] xl:basis-[28%]"
              >
                <button
                  onClick={() => openVideo(idx)}
                  className="group relative w-full rounded-xl overflow-hidden cursor-pointer text-left"
                >
                  <div className="relative min-h-[360px] sm:min-h-[400px] md:min-h-[420px]">
                    <img
                      src={DEMO_THUMBNAILS[feat.key]}
                      alt={t(feat.titleKey)}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-[0_4px_20px_rgba(0,0,0,0.3)] transition-transform duration-200 group-hover:scale-110">
                        <Play className="w-5 h-5 text-stone-900 ml-0.5" fill="currentColor" />
                      </div>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 md:p-8">
                      <h4
                        className="text-lg sm:text-xl font-bold text-white tracking-tight mb-2 md:mb-3"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                      >
                        {t(feat.titleKey)}
                      </h4>
                      <p className="text-[13px] sm:text-sm text-white/70 leading-relaxed line-clamp-2 mb-4 md:mb-8">
                        {t(feat.descKey)}
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white/80 group-hover:text-white transition-colors">
                        {t('homeB.features.watchDemo')}
                        <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pagination dots */}
      <div className="flex justify-center gap-2 mt-6">
        {FEATURES.map((_, i) => (
          <button
            key={i}
            onClick={() => emblaApi?.scrollTo(i)}
            className={`h-2 w-2 rounded-full transition-colors duration-200 ${currentSlide === i ? 'bg-stone-800' : 'bg-stone-300'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Video modal */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
          >
            <motion.div
              className="absolute inset-0 bg-stone-800/40 backdrop-blur-2xl"
              onClick={closeVideo}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            />
            <button onClick={goPrev} className="absolute left-2 sm:left-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-colors duration-150 cursor-pointer" aria-label="Previous video">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button onClick={goNext} className="absolute right-2 sm:right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-colors duration-150 cursor-pointer" aria-label="Next video">
              <ArrowRight className="w-5 h-5" />
            </button>
            <motion.div
              className="relative w-full max-w-4xl mx-12 sm:mx-16 rounded-2xl overflow-hidden bg-stone-900 border border-stone-700/60 shadow-2xl"
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-stone-700/50 bg-stone-800/50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <button onClick={closeVideo} className="w-3 h-3 rounded-full bg-red-500/80 hover:bg-red-400 transition-colors cursor-pointer" aria-label="Close" />
                    <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                    <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                  </div>
                  <span className="text-[10px] font-mono text-stone-500 tracking-wider uppercase ml-1">
                    logami.app / {t(activeVideo.titleKey).toLowerCase().replace(/\s+/g, '-')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-stone-500">{(activeIndex ?? 0) + 1} / {FEATURES.length}</span>
                  <button onClick={closeVideo} className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-400 hover:text-white hover:bg-stone-700 transition-all duration-150 cursor-pointer" aria-label="Close">
                    <ChevronUp className="w-4 h-4 rotate-45" />
                  </button>
                </div>
              </div>
              <video
                ref={videoRef}
                key={`${activeVideo.key}-${activeVideoSourceIndex}`}
                src={currentActiveVideoSource}
                controls autoPlay muted playsInline preload="metadata"
                className="w-full aspect-video object-cover bg-black"
                onError={handleActiveVideoError}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// STICKY BOTTOM CTA (mobile)
// ═══════════════════════════════════════════════════════════════
function StickyBottomCta({ label, onClick, visible }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 pt-3 bg-white/95 backdrop-blur-xl border-t border-stone-200/80 shadow-[0_-4px_24px_rgba(28,25,23,0.06)] lg:hidden safe-area-inset"
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <button
            onClick={onClick}
            className="w-full min-h-[48px] flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-stone-900 text-white text-[15px] font-semibold shadow-lg active:scale-[0.98] transition-transform cursor-pointer"
          >
            {label}
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const TelcB1LandingPage = ({ user, isAuthenticated }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const prefersReduced = useReducedMotion();
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const heroCTARef = useRef(null);

  const ctaDest = isAuthenticated && user ? '/dashboard' : '/auth?mode=signup';
  const ctaLabel = isAuthenticated && user ? t('home.nav.goToDashboard') : t('telcB1.hero.cta');

  const trackCta = useCallback((ctaName, section) => {
    trackConversionEvent('landing_cta_click', {
      cta_name: ctaName,
      section,
      destination: ctaDest,
      page: 'telc_b1_campaign',
      user_state: isAuthenticated && user ? 'authenticated' : 'anonymous',
    });
  }, [isAuthenticated, user, ctaDest]);

  const handleCta = useCallback((ctaName, section) => {
    trackCta(ctaName, section);
    navigate(ctaDest);
  }, [trackCta, navigate, ctaDest]);

  // Secondary CTA → the guest mock exam (no sign-up). Distinct destination from
  // the primary CTA, so it gets its own tracked event.
  const handleTryFree = useCallback((section) => {
    trackConversionEvent('landing_cta_click', {
      cta_name: 'hero_try_free_exam',
      section,
      destination: '/try',
      page: 'telc_b1_campaign',
      user_state: isAuthenticated && user ? 'authenticated' : 'anonymous',
    });
    navigate('/try');
  }, [navigate, isAuthenticated, user]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setShowStickyBar(!entry.isIntersecting), { threshold: 0 });
    const el = heroCTARef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  useEffect(() => {
    const onScroll = () => setShowScrollToTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const fadeUp = (delay = 0) => prefersReduced
    ? { initial: {}, animate: {} }
    : { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay } };

  const fadeUpView = (delay = 0) => prefersReduced
    ? {}
    : { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-60px' }, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay } };

  return (
    <div className="landing-page-root relative min-h-screen overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Warm noise grain */}
      <div
        className="pointer-events-none fixed inset-0 z-0 mix-blend-multiply"
        style={{ backgroundImage: NOISE_SVG, backgroundRepeat: 'repeat', backgroundSize: '256px 256px' }}
        aria-hidden
      />

      {/* ════════════════════════════════════════════════════════ */}
      {/* SECTION 1: HERO                                          */}
      {/* ════════════════════════════════════════════════════════ */}
      <section className="relative z-10 bg-[#FAFAF8] overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{ background: 'radial-gradient(ellipse 80% 50% at 25% -15%, rgba(217,119,6,0.05), transparent 60%), radial-gradient(ellipse 50% 40% at 75% 10%, rgba(15,118,110,0.03), transparent)' }}
          aria-hidden
        />

        {/* Nav */}
        <nav className="relative z-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2 flex items-center justify-between">
          <img src="/logo.png" alt="logami" className="h-24 sm:h-28 w-auto object-contain -my-5 sm:-my-6" />
          <div className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated && user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="min-h-[44px] inline-flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-stone-900 border border-stone-300 hover:border-stone-400 rounded-full px-4 py-2 transition-colors duration-200 cursor-pointer"
              >
                {t('home.nav.goToDashboard')} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <>
                <button
                  onClick={() => { trackCta('nav_login', 'nav'); navigate('/auth?mode=login'); }}
                  className="min-h-[44px] text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors duration-200 cursor-pointer px-3"
                >
                  {t('home.nav.login')}
                </button>
                <motion.button
                  onClick={() => handleCta('nav_get_started', 'nav')}
                  className="min-h-[44px] inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-stone-900 hover:bg-stone-800 rounded-full px-5 py-2.5 shadow-md shadow-stone-900/10 transition-colors duration-200 cursor-pointer"
                  whileTap={{ scale: 0.97 }}
                >
                  {t('home.nav.getStarted')} <ArrowRight className="w-3.5 h-3.5" />
                </motion.button>
              </>
            )}
          </div>
        </nav>

        {/* Hero content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 lg:pt-16 pb-16 sm:pb-20 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-10 lg:gap-14 items-center">

            {/* Left: copy */}
            <div>
              {/* TELC B1 badge, amber accent, same style as homeB labels */}
              <motion.div className="flex items-center gap-2 mb-4" {...fadeUp(0)}>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-xs font-semibold text-amber-700">
                  TELC B1
                </span>
                <span className="text-[11px] font-mono font-semibold tracking-widest uppercase text-stone-400">
                  {t('telcB1.hero.badgeSub')}
                </span>
              </motion.div>

              {/* Headline. Space Grotesk, same size as HomePageB */}
              <motion.h1
                className="text-[1.75rem] leading-[1.12] sm:text-[2.5rem] sm:leading-[1.1] lg:text-[2.9rem] lg:leading-[1.08] tracking-tighter mb-4"
                style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.025em' }}
                {...fadeUp(0)}
              >
                <span className="text-stone-900 font-bold">{t('telcB1.hero.headlineBold')}</span>
                <br />
                <HandDrawnLine>
                  <span className="text-stone-900 font-bold">{t('telcB1.hero.headlineAccent')}</span>
                </HandDrawnLine>
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                className="text-[15px] sm:text-base text-stone-500 leading-relaxed max-w-md mb-6"
                style={{ letterSpacing: '0.005em' }}
                {...fadeUp(0.06)}
              >
                {t('telcB1.hero.subheadline')}
              </motion.p>

              {/* Value checklist, amber checkmarks */}
              <motion.ul className="space-y-2 mb-7" {...fadeUp(0.1)}>
                {['telcB1.hero.point1', 'telcB1.hero.point2', 'telcB1.hero.point3'].map((key) => (
                  <li key={key} className="flex items-center gap-2.5 text-[14px] text-stone-600">
                    <Check className="w-4 h-4 text-teal-600 flex-shrink-0" strokeWidth={2.5} />
                    {t(key)}
                  </li>
                ))}
              </motion.ul>

              {/* Teacher authority */}
              <motion.div className="mb-7" {...fadeUp(0.12)}>
                <TeacherAvatarStack label={t('homeB.hero.builtByTeachers')} />
              </motion.div>

              {/* Primary + secondary CTAs */}
              <motion.div ref={heroCTARef} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" {...fadeUp(0.18)}>
                <motion.button
                  onClick={() => handleCta('hero_primary', 'hero')}
                  className="group w-full sm:w-auto min-h-[48px] inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full bg-stone-900 text-white text-[15px] font-semibold shadow-[0_4px_16px_rgba(28,25,23,0.2)] hover:shadow-[0_6px_24px_rgba(28,25,23,0.25)] hover:bg-stone-800 transition-all duration-200 cursor-pointer"
                  whileTap={{ scale: 0.98 }}
                >
                  {ctaLabel}
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </motion.button>
                <motion.button
                  onClick={() => handleTryFree('hero')}
                  className="group w-full sm:w-auto min-h-[48px] inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border-2 border-stone-900/15 bg-white text-stone-900 text-[15px] font-semibold hover:border-stone-900/40 hover:bg-stone-50 transition-all duration-200 cursor-pointer"
                  whileTap={{ scale: 0.98 }}
                >
                  {t('telcB1.hero.tryFree')}
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </motion.button>
              </motion.div>

              {/* Trust line */}
              <motion.div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-5" {...fadeUp(0.24)}>
                <span className="inline-flex items-center gap-1.5 text-xs text-stone-400">
                  <Check className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" strokeWidth={2.5} />
                  {t('homeB.hero.chipNoCard')}
                </span>
                <span className="text-stone-300 select-none" aria-hidden>|</span>
                <span className="text-xs text-stone-400">{t('telcB1.hero.chipExams')}</span>
                <span className="text-stone-300 select-none" aria-hidden>|</span>
                <span className="text-xs text-stone-400">{t('telcB1.hero.chipLevels')}</span>
              </motion.div>
            </div>

            {/* Right: YouTube lesson video (replaces AppReel) */}
            <motion.div className="w-full flex justify-center" {...fadeUp(0.12)}>
              <YouTubeLessonPlayer videoId={LESSON_VIDEO_ID} />
            </motion.div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ */}
      {/* SECTION 2: WHAT YOU GET                                  */}
      {/* ════════════════════════════════════════════════════════ */}
      <section className="relative z-10 py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{ backgroundImage: NOISE_SVG, backgroundRepeat: 'repeat', backgroundSize: '256px 256px', mixBlendMode: 'multiply' }}
          aria-hidden
        />
        <div className="relative z-10 max-w-5xl mx-auto">
          <motion.div className="mb-10" {...fadeUpView(0)}>
            <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-amber-600 mb-2">
              {t('homeB.course.badge')}
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-[2.5rem] font-bold text-stone-900 tracking-tighter max-w-xl"
              style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}
            >
              {t('telcB1.course.titleBefore')}{' '}
              <HandDrawnCircle>{t('telcB1.course.titleHighlight')}</HandDrawnCircle>{' '}
              {t('telcB1.course.titleAfter')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {/* Hero card. Video Lessons (TELC B1 specific, with teacher stack) */}
            <motion.div
              className="md:row-span-2 rounded-2xl bg-[#FAFAF8] border border-stone-200/80 p-6 sm:p-8 hover:border-stone-300 transition-colors duration-300 flex flex-col justify-between"
              {...fadeUpView(0)}
            >
              <div>
                <span className="text-[11px] font-mono font-semibold tracking-widest uppercase text-amber-600 mb-3 block">
                  {t('telcB1.course.video.label')}
                </span>
                <h3
                  className="text-xl sm:text-2xl font-bold text-stone-900 tracking-tight mb-2.5"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {t('telcB1.course.video.title')}
                </h3>
                <p className="text-[14px] text-stone-500 leading-relaxed max-w-sm">
                  {t('telcB1.course.video.desc')}
                </p>
                {/* Thumbnail link to scroll to lesson */}
                <button
                  onClick={() => document.getElementById('telcb1-lesson-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="mt-5 relative rounded-xl overflow-hidden w-full cursor-pointer group border border-stone-200/80"
                  style={{ aspectRatio: '16/9' }}
                  aria-label="Watch lesson preview"
                >
                  <img
                    src={`https://img.youtube.com/vi/${LESSON_VIDEO_ID}/mqdefault.jpg`}
                    alt="TELC B1 Lesson Preview"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-stone-900/35 group-hover:bg-stone-900/25 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-11 h-11 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-5 h-5 text-stone-900 fill-current ml-0.5" />
                    </div>
                  </div>
                </button>
              </div>
              <div className="mt-6 pt-5 border-t border-stone-200/60">
                <TeacherAvatarStack label={t('homeB.course.lesson.credential')} />
              </div>
            </motion.div>

            {/* Card 2. Mock Exams */}
            <motion.div
              className="rounded-2xl bg-[#FAFAF8] border border-stone-200/80 p-5 sm:p-6 hover:border-stone-300 transition-colors duration-300"
              {...fadeUpView(0.05)}
            >
              <span className="text-[11px] font-mono font-semibold tracking-widest uppercase text-stone-400 mb-3 block">
                {t('homeB.course.mock.label')}
              </span>
              <h3 className="text-base font-bold text-stone-900 tracking-tight mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {t('homeB.course.mock.title')}
              </h3>
              <p className="text-[14px] text-stone-500 leading-relaxed">{t('homeB.course.mock.desc')}</p>
            </motion.div>

            {/* Card 3. Practice */}
            <motion.div
              className="rounded-2xl bg-[#FAFAF8] border border-stone-200/80 p-5 sm:p-6 hover:border-stone-300 transition-colors duration-300"
              {...fadeUpView(0.1)}
            >
              <span className="text-[11px] font-mono font-semibold tracking-widest uppercase text-stone-400 mb-3 block">
                {t('homeB.course.practice.label')}
              </span>
              <h3 className="text-base font-bold text-stone-900 tracking-tight mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {t('homeB.course.practice.title')}
              </h3>
              <p className="text-[14px] text-stone-500 leading-relaxed">{t('homeB.course.practice.desc')}</p>
            </motion.div>
          </div>

          {/* Full-width dark card, skills */}
          <motion.div
            className="mt-3.5 rounded-2xl bg-stone-900 p-6 sm:p-8 text-white shadow-[0_4px_24px_rgba(28,25,23,0.2)]"
            {...fadeUpView(0.15)}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold tracking-tight mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {t('homeB.course.skills.title')}
                </h3>
                <p className="text-[14px] text-stone-400 leading-relaxed">{t('homeB.course.skills.desc')}</p>
              </div>
              <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                {['Vocabulary', 'Grammar', 'Redemittel'].map((tag) => (
                  <span key={tag} className="px-3.5 py-1.5 rounded-full bg-white/[0.06] border border-white/[0.06] text-xs font-medium text-stone-300 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ */}
      {/* SECTION 3: LESSON VIDEO, full lesson preview            */}
      {/* ════════════════════════════════════════════════════════ */}
      <section id="telcb1-lesson-section" className="relative z-10 py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF8] overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{ backgroundImage: NOISE_SVG, backgroundRepeat: 'repeat', backgroundSize: '256px 256px', mixBlendMode: 'multiply' }}
          aria-hidden
        />
        <div className="relative z-10 max-w-5xl mx-auto">
          <motion.div className="mb-10" {...fadeUpView(0)}>
            <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-amber-600 mb-2">
              {t('telcB1.lessonSection.badge')}
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-[2.5rem] font-bold text-stone-900 tracking-tighter"
              style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}
            >
              {t('telcB1.lessonSection.title')}
            </h2>
            <p className="mt-3 text-[14px] text-stone-500 leading-relaxed max-w-lg">
              {t('telcB1.lessonSection.subtitle')}
            </p>
          </motion.div>

          <motion.div
            className="rounded-2xl overflow-hidden border border-stone-200/80 shadow-[0_4px_24px_rgba(28,25,23,0.1)]"
            {...fadeUpView(0.06)}
          >
            <div style={{ aspectRatio: '16/9' }} className="bg-stone-900 relative">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${LESSON_VIDEO_ID}?rel=0&modestbranding=1&color=white`}
                title="TELC B1 Lesson. Logami"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* CTA below the video */}
          <motion.div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4" {...fadeUpView(0.1)}>
            <motion.button
              onClick={() => handleCta('lesson_section_cta', 'lesson_section')}
              className="group min-h-[44px] inline-flex items-center gap-2 px-6 py-3 rounded-full bg-stone-900 text-white text-[14px] font-semibold shadow-[0_4px_16px_rgba(28,25,23,0.2)] hover:bg-stone-800 transition-all duration-200 cursor-pointer"
              whileTap={{ scale: 0.98 }}
            >
              {t('telcB1.lessonSection.cta')}
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </motion.button>
            <span className="text-xs text-stone-400">{t('homeB.hero.chipNoCard')}</span>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ */}
      {/* SECTION 4: HOW IT WORKS + FEATURE GALLERY               */}
      {/* ════════════════════════════════════════════════════════ */}
      <section className="relative z-10 py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{ backgroundImage: NOISE_SVG, backgroundRepeat: 'repeat', backgroundSize: '256px 256px', mixBlendMode: 'multiply' }}
          aria-hidden
        />
        <div className="relative z-10 max-w-5xl mx-auto">
          <motion.div className="mb-10" {...fadeUpView(0)}>
            <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-stone-400 mb-2">
              {t('homeB.howItWorks.badge')}
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-[2.5rem] font-bold text-stone-900 tracking-tighter"
              style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}
            >
              {t('homeB.howItWorks.title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-0 mb-14">
            {[
              { num: '01', titleKey: 'homeB.howItWorks.step1.title', descKey: 'homeB.howItWorks.step1.desc' },
              { num: '02', titleKey: 'homeB.howItWorks.step2.title', descKey: 'homeB.howItWorks.step2.desc' },
              { num: '03', titleKey: 'homeB.howItWorks.step3.title', descKey: 'homeB.howItWorks.step3.desc' },
            ].map((step, i) => (
              <motion.div key={step.num} className="relative sm:px-6 sm:first:pl-0 sm:last:pr-0" {...fadeUpView(i * 0.06)}>
                {i < 2 && <div className="hidden sm:block absolute top-4 right-0 w-full h-px bg-stone-200 -z-10" />}
                <div className="text-xs font-mono font-bold text-amber-600 tracking-wider mb-3">{step.num}</div>
                <h3 className="text-base font-bold text-stone-900 tracking-tight mb-1.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {t(step.titleKey)}
                </h3>
                <p className="text-[14px] text-stone-500 leading-relaxed">{t(step.descKey)}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUpView(0.08)}>
            <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-stone-400 mb-3">
              {t('homeB.howItWorks.featureListLabel')}
            </p>
          </motion.div>
        </div>

        <div className="w-full">
          <FeatureVideoGallery t={t} />
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ */}
      {/* SECTION 5: SOCIAL PROOF                                  */}
      {/* ════════════════════════════════════════════════════════ */}
      <section className="relative z-10 py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF8] overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{ backgroundImage: NOISE_SVG, backgroundRepeat: 'repeat', backgroundSize: '256px 256px', mixBlendMode: 'multiply' }}
          aria-hidden
        />
        <div className="relative z-10 max-w-5xl mx-auto">
          <motion.div className="mb-14 max-w-2xl" {...fadeUpView(0)}>
            <Quote className="w-8 h-8 text-amber-200 mb-4" strokeWidth={1.5} />
            <blockquote
              className="text-xl sm:text-2xl font-medium text-stone-800 leading-snug tracking-tight mb-5"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {t('telcB1.proof.testimonialQuote')}
            </blockquote>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-50 border border-amber-200/60 flex items-center justify-center text-sm font-bold text-amber-700">
                {t('telcB1.proof.testimonialInitial')}
              </div>
              <div>
                <p className="text-sm font-semibold text-stone-700">{t('telcB1.proof.testimonialName')}</p>
                <p className="text-xs text-stone-400">{t('telcB1.proof.testimonialExam')}</p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8 mb-14">
            {[
              { value: t('homeB.proof.stat1Value'), label: t('homeB.proof.stat1Label'), large: true },
              { value: t('homeB.proof.stat2Value'), label: t('homeB.proof.stat2Label') },
              { value: t('homeB.proof.stat3Value'), label: t('homeB.proof.stat3Label') },
            ].map((stat, i) => (
              <motion.div key={stat.label} className={i === 0 ? 'col-span-2 sm:col-span-1' : ''} {...fadeUpView(i * 0.06)}>
                <div
                  className={`font-bold text-stone-900 tracking-tight leading-tight break-words ${stat.large ? 'text-4xl sm:text-5xl' : 'text-3xl sm:text-4xl'}`}
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {stat.value}
                </div>
                <div className="text-[13px] text-stone-400 mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ */}
      {/* SECTION 6: MEET THE TEAM                                 */}
      {/* ════════════════════════════════════════════════════════ */}
      <section className="relative z-10 py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{ backgroundImage: NOISE_SVG, backgroundRepeat: 'repeat', backgroundSize: '256px 256px', mixBlendMode: 'multiply' }}
          aria-hidden
        />
        <div className="relative z-10 max-w-5xl mx-auto">
          <motion.div className="mb-10" {...fadeUpView(0)}>
            <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-amber-600 mb-2">
              {t('homeB.team.badge')}
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-[2.5rem] font-bold text-stone-900 tracking-tighter max-w-lg"
              style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}
            >
              {t('homeB.team.title')}
            </h2>
          </motion.div>

          <motion.div className="rounded-2xl border border-stone-200/80 bg-white p-6 sm:p-8 mb-3.5" {...fadeUpView(0)}>
            <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-start">
              <img src="/images/amir-safari.jpg" alt="Emir Safari" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover flex-shrink-0 ring-2 ring-stone-100" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-1.5">
                  <h3 className="text-lg sm:text-xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Emir Safari
                  </h3>
                  <span className="text-xs font-medium text-amber-600">{t('homeB.team.founderLabel')}</span>
                </div>
                <p className="text-[11px] font-medium text-stone-400 tracking-wide mb-3">{t('homeB.team.emir.credentials')}</p>
                <p className="text-[14px] text-stone-500 leading-relaxed max-w-xl">{t('homeB.team.emir.bio')}</p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            {TEAM.filter(m => !m.large).map((member, i) => (
              <motion.div key={member.initials} className="rounded-2xl border border-stone-200/80 bg-white p-5 sm:p-6" {...fadeUpView(0.04 * (i + 1))}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-semibold flex-shrink-0" style={{ backgroundColor: member.color, color: member.textColor }}>
                    {member.initials}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stone-900">{member.name}</p>
                    <p className="text-xs text-stone-400">{t(`homeB.team.${member.role}`)}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div className="mt-8 max-w-xl" {...fadeUpView(0.16)}>
            <div className="flex items-start gap-3">
              <div className="w-px h-12 bg-amber-300/50 flex-shrink-0 mt-1" />
              <p className="text-[14px] text-stone-400 leading-relaxed italic">{t('homeB.team.personalNote')}</p>
            </div>
            <p className="text-[13px] font-medium text-stone-500 mt-3 ml-4">, Emir</p>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ */}
      {/* SECTION 7: FINAL CTA                                     */}
      {/* ════════════════════════════════════════════════════════ */}
      <section className="relative z-10 py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-stone-900 overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{ background: 'radial-gradient(ellipse 50% 40% at 50% 60%, rgba(217,119,6,0.07), transparent), radial-gradient(ellipse 40% 30% at 30% 40%, rgba(15,118,110,0.05), transparent)' }}
          aria-hidden
        />
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <motion.h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tighter mb-3"
            style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}
            {...fadeUpView(0)}
          >
            {t('telcB1.finalCta.title')}
          </motion.h2>
          <motion.p className="text-[15px] sm:text-base text-stone-400 leading-relaxed mb-8 max-w-md mx-auto" {...fadeUpView(0.06)}>
            {t('telcB1.finalCta.subtitle')}
          </motion.p>
          <motion.div {...fadeUpView(0.12)}>
            <motion.button
              onClick={() => handleCta('final_cta_primary', 'final_cta')}
              className="group min-h-[48px] inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-white text-stone-900 text-[15px] font-semibold shadow-[0_4px_24px_rgba(255,255,255,0.08)] hover:shadow-[0_6px_32px_rgba(255,255,255,0.12)] hover:bg-stone-50 transition-all duration-200 cursor-pointer"
              whileTap={{ scale: 0.98 }}
            >
              {ctaLabel}
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </motion.button>
          </motion.div>
          <motion.p className="text-xs text-stone-500 mt-5" {...fadeUpView(0.18)}>
            {t('homeB.finalCta.trust')}
          </motion.p>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════ */}
      {/* FOOTER (identical to HomePageB)                          */}
      {/* ════════════════════════════════════════════════════════ */}
      <MarketingFooter />

      {/* Sticky bottom CTA (mobile) */}
      <StickyBottomCta
        label={isAuthenticated && user ? t('home.nav.goToDashboard') : t('homeB.sticky.cta')}
        onClick={() => handleCta('sticky_bottom', 'sticky_bar')}
        visible={showStickyBar}
      />

      {/* Scroll to top */}
      <AnimatePresence>
        {showScrollToTop && (
          <motion.button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-20 lg:bottom-6 right-6 z-50 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm text-stone-600 shadow-lg ring-1 ring-stone-200 hover:bg-white transition-colors duration-200 cursor-pointer"
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.2 }}
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            aria-label="Scroll to top"
          >
            <ChevronUp className="w-5 h-5" strokeWidth={2.5} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TelcB1LandingPage;
